import { Copy, Download, Renew } from '@carbon/icons-react';
import { Button, ComboBox, DropdownSkeleton, InlineLoading, InlineNotification, Toggle } from '@carbon/react';
import {
  asProblem,
  EmptyState,
  hasPermission,
  ListTable,
  PageHeader,
  problemMessage,
  RowAction,
  StatusTag,
  useAdmin,
  useFormat,
  useLocalPages,
} from '@pos/admin-ui';
import { ConfirmDialog } from '@pos/design-system';
import { useTranslation } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ErrorNotice, TransientNotice } from '../common/Notices';
import {
  downloadTableQrSheet,
  generateTableQr,
  listTableQr,
  loadStores,
  rotateStoreTableQr,
  listTableQrLinks,
  rotateTableQr,
  setTableQrActive,
  TABLE_QR_MANAGE,
  TABLE_QR_READ,
  tableQrKey,
  type StoreOption,
  type TableQr,
} from './tableQrApi';
import './tableQr.scss';

/** A table of the list: `id` is the dining table (every row has one), `codeId` its code's (none before one is made). */
type Row = TableQr & { id: string; codeId: string | null };

/** One status for the Tag: no code yet, on, off, or the table itself out of use. */
function statusOf(row: Row): 'none' | 'active' | 'off' | 'tableOff' {
  if (!row.tableActive) return 'tableOff';
  if (!row.codeId) return 'none';
  return row.isActive ? 'active' : 'off';
}

/** StatusTag colours (§13): on is done (green), the rest neutral. */
const TAG_STATUS: Record<ReturnType<typeof statusOf>, string> = {
  active: 'active',
  none: 'new',
  off: 'cancelled',
  tableOff: 'cancelled',
};

/**
 * Online ordering › Table QR codes (FR-QR-07, ADR 0067): a store's dining tables and their QR codes. Make codes for the
 * tables without one, download the print sheet (A4, six cards a page), change one code or all of them when cards are lost
 * or copied (the old cards stop working), and switch a table's code off or on. Tokens are never shown here: they are only on
 * the sheet, which needs the manage permission.
 */
export function TableQrPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const { session } = useAdmin();
  const canRead = hasPermission(session, TABLE_QR_READ) || hasPermission(session, TABLE_QR_MANAGE);
  const canManage = hasPermission(session, TABLE_QR_MANAGE);
  const queryClient = useQueryClient();
  const stores = useQuery({ queryKey: [...tableQrKey, 'stores'], queryFn: loadStores, enabled: canRead });
  const [store, setStore] = useState<StoreOption>();
  const list = useQuery({
    queryKey: [...tableQrKey, 'list', store?.id],
    queryFn: () => listTableQr(store?.id ?? ''),
    enabled: Boolean(store) && canRead,
  });
  const [notice, setNotice] = useState<string>();
  const [rotating, setRotating] = useState<Row>();
  const [rotatingAll, setRotatingAll] = useState(false);

  const refresh = () => queryClient.invalidateQueries({ queryKey: [...tableQrKey, 'list', store?.id] });
  const say = (text: string) => {
    setNotice(text);
    void refresh();
  };

  const generate = useMutation({
    mutationFn: () => generateTableQr(store?.id ?? ''),
    onSuccess: (links) => say(t('tableQr.generated', { count: links.length })),
  });
  const rotateOne = useMutation({
    mutationFn: (row: Row) => rotateTableQr(row.codeId ?? ''),
    onSuccess: (link) => {
      setRotating(undefined);
      say(t('tableQr.rotated', { code: link.tableCode }));
    },
  });
  const rotateAll = useMutation({
    mutationFn: () => rotateStoreTableQr(store?.id ?? ''),
    onSuccess: (links) => {
      setRotatingAll(false);
      say(t('tableQr.rotatedAll', { count: links.length }));
    },
  });
  const toggle = useMutation({
    mutationFn: ({ row, on }: { row: Row; on: boolean }) => setTableQrActive(row.codeId ?? '', on),
    onSuccess: (code) =>
      say(t(code.isActive ? 'tableQr.turnedOn' : 'tableQr.turnedOff', { code: code.tableCode })),
  });
  // A table's address, to open it on a phone or try it in development without printing (ADR 0093).
  const copyLink = useMutation({
    mutationFn: async (row: Row) => {
      const link = (await listTableQrLinks(store?.id ?? '')).find((l) => l.id === row.codeId);
      if (!link) throw { title: t('tableQr.linkMissing', { code: row.tableCode }) };
      await navigator.clipboard.writeText(link.url);
      return link;
    },
    onSuccess: (link) => setNotice(t('tableQr.copied', { code: link.tableCode })),
  });
  const sheet = useMutation({
    mutationFn: () => downloadTableQrSheet(store?.id ?? '', `table-qr-${store?.code ?? 'store'}.pdf`),
  });

  const rows: Row[] = (list.data ?? []).map((r) => ({ ...r, id: r.diningTableId, codeId: r.id }));
  // A store's tables come in one answer; the table pages them like every list screen.
  const pages = useLocalPages(rows);

  if (!canRead) {
    return (
      <section className="pos-admin-page">
        <EmptyState heading={t('admin.errors.forbiddenTitle')} description={t('admin.errors.forbidden')} />
      </section>
    );
  }

  const missing = rows.filter((r) => r.tableActive && !r.codeId).length;
  const withCode = rows.filter((r) => r.codeId).length;
  const printable = rows.some((r) => r.codeId && r.isActive && r.tableActive);
  const actionError = generate.error ?? toggle.error ?? sheet.error ?? copyLink.error;
  const busy = generate.isPending || rotateAll.isPending || toggle.isPending;

  const choose = (next: StoreOption | undefined) => {
    setStore(next);
    setNotice(undefined);
    for (const m of [generate, rotateOne, rotateAll, toggle, sheet, copyLink]) m.reset();
  };

  return (
    <section className="pos-admin-page pos-table-qr">
      <PageHeader title={t('tableQr.title')} description={t('tableQr.intro')} />
      <div className="pos-table-qr__store">
        {stores.isLoading ? (
          <DropdownSkeleton />
        ) : (
          <ComboBox<StoreOption>
            id="table-qr-store"
            titleText={t('tableQr.store')}
            placeholder={t('tableQr.chooseStore')}
            items={stores.data ?? []}
            itemToString={(s) => s?.label ?? ''}
            selectedItem={store ?? null}
            onChange={({ selectedItem }) => choose(selectedItem ?? undefined)}
          />
        )}
      </div>
      {stores.error && (
        <ErrorNotice title={problemMessage(t, asProblem(stores.error))} onRetry={() => void stores.refetch()} />
      )}
      {notice && <TransientNotice kind="success" title={notice} onDone={() => setNotice(undefined)} />}
      {actionError && <ErrorNotice title={problemMessage(t, asProblem(actionError))} />}

      {!store ? (
        <EmptyState heading={t('tableQr.pickStore')} description={t('tableQr.pickStoreHint')} />
      ) : (
        <ListTable<Row>
          ariaLabel={t('tableQr.tableLabel', { store: store.label })}
          loading={list.isLoading}
          error={list.error ? problemMessage(t, asProblem(list.error)) : undefined}
          onRetry={() => void list.refetch()}
          rows={pages.rows}
          pagination={pages.pagination}
          toolbarActions={
            canManage && (
              <>
                <Button
                  kind="danger--tertiary"
                  renderIcon={Renew}
                  disabled={busy || withCode === 0}
                  onClick={() => {
                    rotateAll.reset();
                    setRotatingAll(true);
                  }}
                >
                  {t('tableQr.rotateAll')}
                </Button>
                <Button
                  kind="tertiary"
                  renderIcon={Download}
                  disabled={sheet.isPending || !printable}
                  onClick={() => {
                    sheet.reset();
                    sheet.mutate();
                  }}
                >
                  {sheet.isPending ? <InlineLoading description={t('tableQr.downloading')} /> : t('tableQr.download')}
                </Button>
                <Button
                  kind="primary"
                  disabled={busy || missing === 0}
                  onClick={() => {
                    generate.reset();
                    generate.mutate();
                  }}
                >
                  {generate.isPending ? (
                    <InlineLoading description={t('tableQr.generating')} />
                  ) : (
                    t('tableQr.generate', { count: missing })
                  )}
                </Button>
              </>
            )
          }
          columns={[
            { key: 'tableCode', header: t('tableQr.columns.table'), sortable: true },
            { key: 'areaName', header: t('tableQr.columns.area'), sortable: true },
            {
              key: 'status',
              header: t('tableQr.columns.status'),
              render: (r) => {
                const status = statusOf(r);
                return <StatusTag status={TAG_STATUS[status]}>{t(`tableQr.status.${status}`)}</StatusTag>;
              },
            },
            {
              key: 'rotation',
              header: t('tableQr.columns.rotation'),
              render: (r) => (r.rotation != null ? Number(r.rotation) : ''),
            },
            {
              key: 'rotatedAt',
              header: t('tableQr.columns.rotatedAt'),
              render: (r) => (r.rotatedAt ? format.dateTime(r.rotatedAt) : ''),
            },
            ...(canManage
              ? [
                  {
                    key: 'active',
                    header: t('tableQr.columns.active'),
                    render: (r: Row) =>
                      r.codeId ? (
                        <Toggle
                          id={`table-qr-active-${r.codeId}`}
                          labelText={t('tableQr.activeLabel', { code: r.tableCode })}
                          hideLabel
                          labelA={t('tableQr.off')}
                          labelB={t('tableQr.on')}
                          toggled={Boolean(r.isActive)}
                          disabled={toggle.isPending}
                          onToggle={(on: boolean) => {
                            toggle.reset();
                            toggle.mutate({ row: r, on });
                          }}
                        />
                      ) : null,
                  },
                ]
              : []),
          ]}
          rowActions={
            canManage
              ? (r) =>
                  r.codeId ? (
                    <>
                      {r.isActive && r.tableActive && (
                        <RowAction
                          action={t('tableQr.copyLink')}
                          record={r.tableCode}
                          icon={Copy}
                          onClick={() => {
                            copyLink.reset();
                            copyLink.mutate(r);
                          }}
                        />
                      )}
                      <RowAction
                        action={t('tableQr.rotate')}
                        record={r.tableCode}
                        icon={Renew}
                        onClick={() => {
                          rotateOne.reset();
                          setRotating(r);
                        }}
                      />
                    </>
                  ) : null
              : undefined
          }
          empty={{ heading: t('tableQr.emptyTitle'), description: t('tableQr.empty') }}
        />
      )}

      {rotating && (
        <ConfirmDialog
          open
          danger={false}
          title={t('tableQr.rotateTitle', { code: rotating.tableCode })}
          confirmLabel={t('tableQr.rotate')}
          busy={rotateOne.isPending}
          onConfirm={() => rotateOne.mutate(rotating)}
          onCancel={() => setRotating(undefined)}
        >
          <p>{t('tableQr.rotateBody', { code: rotating.tableCode })}</p>
          {rotateOne.error && (
            <InlineNotification
              kind="error"
              role="alert"
              lowContrast
              hideCloseButton
              title={problemMessage(t, asProblem(rotateOne.error))}
            />
          )}
        </ConfirmDialog>
      )}
      {rotatingAll && store && (
        <ConfirmDialog
          open
          title={t('tableQr.rotateAllTitle', { store: store.label })}
          confirmLabel={t('tableQr.rotateAll')}
          busy={rotateAll.isPending}
          onConfirm={() => rotateAll.mutate()}
          onCancel={() => setRotatingAll(false)}
        >
          <p>{t('tableQr.rotateAllBody', { count: withCode })}</p>
          {rotateAll.error && (
            <InlineNotification
              kind="error"
              role="alert"
              lowContrast
              hideCloseButton
              title={problemMessage(t, asProblem(rotateAll.error))}
            />
          )}
        </ConfirmDialog>
      )}
    </section>
  );
}
